/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  CircularProgress,
  Paper,
  IconButton,
} from "@mui/material";
import { BrandButton } from "components/brand";
import { ComponentHeader } from "components";
import {
  ArrowBack,
  CloudUpload,
  FormatListBulleted,
  Image as ImageIcon,
} from "@mui/icons-material";
import {
  createMenuCategory,
  updateMenuCategory,
  getMenuCategoryById,
  updateCategoryItems,
  getAllProductsWithoutPagination,
} from "actions";
import { clearError, clearCurrentCategory } from "features/menuCategoriesSlice";
import { PATHNAME } from "constants/pathnames";
import { FILE_INPUT_ACCEPT } from "constants/fileTypes";
import { ADMIN_TEXTS } from "constants/adminTexts";
import { NOTIFY } from "constants/notifyType";
import { SPINNER_SIZE } from "constants/general";
import Notify from "helpers/Notify";
import ItemsSelectModal from "components/ItemsSelectModal";
import { SubcategoryManager } from "./SubcategoryManager";
import "./Menu.scss";

const imageBase = process.env.REACT_APP_API_IMAGE_URL;

const validationSchema = Yup.object({
  name: Yup.string()
    .required(ADMIN_TEXTS.menuCategories.nameRequired)
    .min(2, ADMIN_TEXTS.menuCategories.nameMinLength)
    .max(100, ADMIN_TEXTS.menuCategories.nameMaxLength),
});

export const MenuCategoryForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const { currentCategory, categories, loading, error } = useSelector(
    (state) => state.menuCategories,
  );
  const allProducts = useSelector((state) => state.products.allProducts);
  const categoryId = Number(id);

  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imageError, setImageError] = useState(false);
  const categoryItems = useMemo(
    () =>
      allProducts.filter(
        (p) => p.category?.id === categoryId && !p.subcategory,
      ),
    [allProducts, categoryId],
  );
  const [itemsModalOpen, setItemsModalOpen] = useState(false);
  const [itemsSaving, setItemsSaving] = useState(false);

  const formik = useFormik({
    initialValues: { name: "" },
    validationSchema,
    onSubmit: async (values) => {
      if (!imagePreview) {
        setImageError(true);
        return;
      }

      try {
        if (isEdit) {
          await dispatch(
            updateMenuCategory({
              id,
              data: { name: values.name, image: imageFile },
            }),
          ).unwrap();
        } else {
          await dispatch(
            createMenuCategory({ name: values.name, image: imageFile }),
          ).unwrap();
        }
        navigate(PATHNAME.menuCategories);
      } catch {
        // error is handled in the slice / Notify
      }
    },
  });

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImageError(false);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const handleBack = () => navigate(PATHNAME.menuCategories);

  const handleCategoryItemsSave = async (selectedProducts) => {
    const originalIds = new Set(categoryItems.map((i) => i.id));
    const selectedIdSet = new Set(selectedProducts.map((p) => p.id));

    const addedIds = selectedProducts
      .filter((p) => !originalIds.has(p.id))
      .map((p) => p.id);
    const removedIds = categoryItems
      .filter((i) => !selectedIdSet.has(i.id))
      .map((i) => i.id);

    if (addedIds.length === 0 && removedIds.length === 0) {
      setItemsModalOpen(false);
      return;
    }

    setItemsSaving(true);
    try {
      const ops = [];
      if (addedIds.length > 0) {
        ops.push(
          dispatch(
            updateCategoryItems({
              productIds: addedIds,
              categoryId,
              subcategoryId: null,
            }),
          ).unwrap(),
        );
      }
      if (removedIds.length > 0) {
        ops.push(
          dispatch(
            updateCategoryItems({
              productIds: removedIds,
              categoryId: null,
            }),
          ).unwrap(),
        );
      }
      await Promise.all(ops);
      Notify(NOTIFY.type.success, NOTIFY.message.update);
      setItemsModalOpen(false);
      setItemsSaving(false);

      await dispatch(getAllProductsWithoutPagination()).unwrap();
      dispatch(getMenuCategoryById(id));
    } catch {
      setItemsSaving(false);
    }
  };

  useEffect(() => {
    if (isEdit && id) dispatch(getMenuCategoryById(id));
    return () => {
      dispatch(clearCurrentCategory());
      dispatch(clearError());
    };
  }, [id, isEdit]);

  useEffect(() => {
    if (currentCategory && isEdit) {
      formik.setValues({ name: currentCategory.name || "" });
      const image =
        currentCategory.image ||
        categories.find((c) => c.id === currentCategory.id)?.image;
      if (image) {
        setImagePreview(`${imageBase}${image.path}`);
      }
    }
  }, [currentCategory, isEdit]);

  if (isEdit && !currentCategory && !error) {
    return (
      <Box className="menu-form-loading">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="menu-category-form">
      <Box className="form-header">
        <IconButton onClick={handleBack} className="back-button">
          <ArrowBack />
        </IconButton>
        <Box className="form-header__title">
          <ComponentHeader
            title={
              isEdit
                ? ADMIN_TEXTS.menuCategories.editCategory
                : ADMIN_TEXTS.menuCategories.addCategory
            }
          />
        </Box>
        {isEdit && (
          <BrandButton
            size="small"
            color="primary"
            startIcon={<FormatListBulleted />}
            onClick={() => setItemsModalOpen(true)}
          >
            {ADMIN_TEXTS.menuCategories.manageItems}
          </BrandButton>
        )}
      </Box>

      {error && (
        <Typography
          variant="body2"
          color="error"
          className="form-error-message"
        >
          {error}
        </Typography>
      )}

      <Card>
        <CardContent>
          <form onSubmit={formik.handleSubmit}>
            <Box display="flex" flexDirection="column" gap={3}>
              <Box className="image-upload-section">
                <Typography variant="h6" gutterBottom>
                  {ADMIN_TEXTS.menuCategories.imageLabel}
                </Typography>
                <Paper
                  className={`upload-paper${imageError ? " upload-paper--error" : ""}`}
                >
                  {imagePreview ? (
                    <Box>
                      <Box className="preview-container">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="preview-image"
                        />
                      </Box>
                      <BrandButton
                        variant="secondary"
                        component="label"
                        startIcon={<CloudUpload />}
                        className="change-image-button"
                      >
                        {ADMIN_TEXTS.products.changeImage}
                        <input
                          type="file"
                          hidden
                          accept={FILE_INPUT_ACCEPT.IMAGE}
                          onChange={handleImageChange}
                        />
                      </BrandButton>
                    </Box>
                  ) : (
                    <Box>
                      <ImageIcon className="upload-icon" />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        className="upload-text"
                      >
                        {ADMIN_TEXTS.menuCategories.uploadImage}
                      </Typography>
                      <BrandButton
                        variant="secondary"
                        component="label"
                        startIcon={<CloudUpload />}
                      >
                        {ADMIN_TEXTS.products.chooseImage}
                        <input
                          type="file"
                          hidden
                          accept={FILE_INPUT_ACCEPT.IMAGE}
                          onChange={handleImageChange}
                        />
                      </BrandButton>
                    </Box>
                  )}
                </Paper>
                {imageError && (
                  <Typography
                    variant="caption"
                    color="error"
                    className="image-error-text"
                  >
                    {ADMIN_TEXTS.menuCategories.imageRequired}
                  </Typography>
                )}
              </Box>

              <TextField
                fullWidth
                label={ADMIN_TEXTS.menuCategories.nameLabel}
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                placeholder={ADMIN_TEXTS.menuCategories.enterName}
              />

              <Box className="form-actions">
                <BrandButton
                  variant="secondary"
                  onClick={handleBack}
                  disabled={loading}
                >
                  {ADMIN_TEXTS.common.cancel}
                </BrandButton>
                <BrandButton type="submit" variant="primary" disabled={loading}>
                  {loading ? (
                    <CircularProgress size={SPINNER_SIZE} color="inherit" />
                  ) : isEdit ? (
                    ADMIN_TEXTS.menuCategories.updateCategory
                  ) : (
                    ADMIN_TEXTS.menuCategories.createCategory
                  )}
                </BrandButton>
              </Box>
            </Box>
          </form>
        </CardContent>
      </Card>

      {isEdit && (
        <>
          <SubcategoryManager
            categoryId={categoryId}
            initialSubcategories={currentCategory?.subcategories || []}
          />

          <ItemsSelectModal
            open={itemsModalOpen}
            onClose={() => setItemsModalOpen(false)}
            onSave={handleCategoryItemsSave}
            selectedIds={categoryItems.map((i) => i.id)}
            saving={itemsSaving}
            title={ADMIN_TEXTS.menuCategories.itemsModalTitle}
          />
        </>
      )}
    </Box>
  );
};
